import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemedInput, ThemedField } from './ThemedSurface';

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.6 10.6A2 2 0 0 0 13.4 13.4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A9.4 9.4 0 0 1 12 5c5.5 0 9 7 9 7a15.2 15.2 0 0 1-2 2.8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.6 6.6C4.3 8.2 3 12 3 12s3.5 7 9 7a8.8 8.8 0 0 0 4.4-1.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.1 12S5.7 5 12 5s9.9 7 9.9 7-3.6 7-9.9 7-9.9-7-9.9-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export interface TransactionSecurityProps {
  txPassword: string;
  onTxPasswordChange: (val: string) => void;
  /** @deprecated Always shown; kept for call-site compatibility. */
  showSecurity?: boolean;
  /** @deprecated No-op; kept for call-site compatibility. */
  onShowSecurityChange?: (val: boolean) => void;
  securityAnswer: string;
  onSecurityAnswerChange: (val: string) => void;
  securityQuestionText?: string;
  disabled?: boolean;
  colors?: {
    muted?: string;
    red?: string;
    activeGreen?: string;
    gold?: string;
    softBorder?: string;
    inputBg?: string;
  };
}

export function TransactionSecurity({
  txPassword,
  onTxPasswordChange,
  securityAnswer,
  onSecurityAnswerChange,
  securityQuestionText,
  disabled,
  colors = {
    muted: '#9CA3AF',
    red: '#F74E5E',
    activeGreen: '#ADD741',
    gold: '#F5EC63',
    softBorder: 'rgba(245,236,99,0.3)',
  },
}: TransactionSecurityProps) {
  const { t } = useTranslation();
  const [showTxPassword, setShowTxPassword] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <ThemedField label={t('common.txPassword')} required>
        <div className="relative">
          <ThemedInput
            type={showTxPassword ? 'text' : 'password'}
            value={txPassword}
            onChange={(e) => onTxPasswordChange(e.target.value)}
            placeholder={t('common.enterTxPassword')}
            className="pr-11"
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => setShowTxPassword((v) => !v)}
            disabled={disabled}
            className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors disabled:opacity-50"
            style={{ color: showTxPassword ? colors.activeGreen : colors.muted }}
            aria-label={showTxPassword ? t('common.hidePassword') : t('common.showPassword')}
          >
            <PasswordVisibilityIcon visible={showTxPassword} />
          </button>
        </div>
      </ThemedField>

      <div className="flex flex-col gap-2">
        <div className="text-sm" style={{ color: colors.muted }}>
          {t('common.securityQuestion')}:{' '}
          <span className="font-semibold" style={{ color: colors.gold }}>
            {securityQuestionText ?? t('common.defaultSecurityQuestion')}
          </span>
        </div>
        <ThemedInput
          type="text"
          value={securityAnswer}
          onChange={(e) => onSecurityAnswerChange(e.target.value)}
          placeholder={t('common.enterSecurityAnswer')}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
